import Cookies from "js-cookie";
import { z } from "zod";

import {
  CookieName,
  FLOWSTEP_COOKIE_EXPIRATION_IN_DAYS,
} from "../cookies/cookies.constants.ts";

import {
  CookieConsentCategory,
  hasCookieConsentForCategory,
} from "./cookieConsent.ts";
import { getCountry, isConsentRequiredCountry } from "./geo.ts";

// localStorage key holding first-touch UTM attribution + geolocation until
// they're persisted to `flowstep.user_profiles` on the visitor's first real
// (non-anonymous) session.
export const SIGNUP_CONTEXT_KEY = "flowstep_attribution";

export const AttributionSchema = z
  .object({
    utm_source: z.string().max(255).nullable(),
    utm_medium: z.string().max(255).nullable(),
    utm_campaign: z.string().max(255).nullable(),
    utm_content: z.string().max(255).nullable(),
    utm_term: z.string().max(255).nullable(),
    referrer: z.string().max(2048).nullable(),
    landing_page: z.string().max(2048),
    captured_at: z.string(),
  })
  .strict();
export type Attribution = z.infer<typeof AttributionSchema>;

export type SignupContext = {
  attribution: Attribution | null;
  country: string | null;
};

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Captures first-touch UTM attribution and geolocation into a cookie scoped
 * to the apex domain (e.g. `.flowstep.ai`) — a plain localStorage key
 * wouldn't survive the redirect from the marketing site (flowstep.ai) to the
 * app (app.flowstep.ai), since those are different origins.
 *
 * This function never removes the cookie itself — it doubles as the
 * "already captured" guard (below), so a later visit on either domain before
 * signup won't re-capture and overwrite first-touch. It's the editor app's
 * job to clear it once a real session has had one shot at persisting it (see
 * loadUser in apps/editor/src/state/user/events.ts) — leaving it for its
 * full 1-year expiry would let a second signup on the same browser (shared
 * device, QA account) inherit the first person's stale attribution.
 *
 * Gated on marketing cookie consent only for visitors in countries where
 * that consent is legally required (see isConsentRequiredCountry) —
 * everyone else is captured immediately.
 */
export const captureAttribution = async ({
  ipinfoToken,
  domain,
}: {
  ipinfoToken: string | undefined;
  domain: string;
}): Promise<void> => {
  if (Cookies.get(CookieName.FlowstepAttribution)) return;

  const country = await getCountry(ipinfoToken);

  if (
    isConsentRequiredCountry(country) &&
    !hasCookieConsentForCategory(CookieConsentCategory.enum.marketing)
  ) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const utm = Object.fromEntries(
    UTM_PARAMS.map((key) => [key, params.get(key) || null]),
  ) as Record<(typeof UTM_PARAMS)[number], string | null>;
  const hasAnyUtmParam = UTM_PARAMS.some((key) => utm[key] !== null);

  if (!hasAnyUtmParam && !country) return;

  const signupContext: SignupContext = {
    attribution: hasAnyUtmParam
      ? {
          ...utm,
          referrer: document.referrer || null,
          landing_page: window.location.pathname,
          captured_at: new Date().toISOString(),
        }
      : null,
    country,
  };

  Cookies.set(CookieName.FlowstepAttribution, JSON.stringify(signupContext), {
    expires: FLOWSTEP_COOKIE_EXPIRATION_IN_DAYS,
    secure: true,
    domain,
  });
};
